import { useEffect, useState } from 'react';

/**
 * NewVersionToast — polls `/api/version` and shows a refresh prompt when
 * the server's reported bundle hash differs from the one this tab loaded
 * with. Solves the "tab open across a deploy → still running old code"
 * edge case.
 *
 * Mounted globally (in App.tsx) so the toast is reachable from every
 * authenticated route. The hash captured on mount is whatever script tag
 * the browser loaded — Vite emits exactly one `index-<hash>.js` per
 * build. We poll every 5 minutes (cheap; server-cached for 60s).
 *
 * The user can dismiss the toast — we don't re-show it for the same
 * mismatched hash. If a SECOND deploy lands after dismissal, the toast
 * returns (different mismatched hash).
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Read the currently-loaded SPA ENTRY bundle hash from the DOM. Vite
 * emits multiple `index-<hash>.js` files (entry + code-split chunks);
 * the entry is identifiable by `type="module"` on its script tag.
 * Targeting the entry specifically (not any matching script tag) is
 * critical — the backend's `/api/version` also returns the entry, so
 * comparing the same canonical filename keeps the two sides in sync.
 *
 * Earlier version matched the FIRST `script[src*="/assets/index-"]` it
 * found in DOM order — which could be a chunk loaded eagerly by another
 * script. Permanent mismatch → toast fired forever post-refresh. Bug
 * caught in prod 2026-06-09.
 */
function readCurrentBundle(): string | null {
  if (typeof document === 'undefined') return null;
  const script = document.querySelector(
    'script[type="module"][src*="/assets/index-"]',
  ) as HTMLScriptElement | null;
  if (!script) return null;
  const m = /\/assets\/(index-[A-Za-z0-9_-]+\.js)(?:\?.*)?$/.exec(script.src);
  return m?.[1] ?? null;
}

export function NewVersionToast() {
  const [showFor, setShowFor] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [currentBundle] = useState<string | null>(() => readCurrentBundle());

  useEffect(() => {
    if (!currentBundle) return;
    let cancelled = false;

    async function check(): Promise<void> {
      try {
        const res = await fetch('/api/version', { credentials: 'include' });
        if (!res.ok) return;
        const body = (await res.json()) as { bundle: string | null };
        if (cancelled) return;
        if (body.bundle && body.bundle !== currentBundle) {
          if (!dismissed.has(body.bundle)) {
            setShowFor(body.bundle);
          }
        }
      } catch {
        // Network blip — try again on the next interval.
      }
    }

    void check();
    const id = window.setInterval(() => void check(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [currentBundle, dismissed]);

  if (!showFor) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-lg border border-nebula-border-soft bg-white px-4 py-3 shadow-lg"
    >
      <div className="flex flex-col text-sm">
        <span className="font-medium text-nebula-fg-1">A new version is available.</span>
        <span className="text-xs text-nebula-fg-3">Refresh to get the latest changes.</span>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-nebula-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-nebula-secondary"
      >
        Refresh
      </button>
      <button
        type="button"
        onClick={() => {
          setDismissed((prev) => new Set(prev).add(showFor));
          setShowFor(null);
        }}
        aria-label="Dismiss"
        className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1"
      >
        ×
      </button>
    </div>
  );
}
